import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactElement } from 'react'
import { useHalloweenEligible } from './eligibility'
import { isSeasonOpen, SEASON_END } from './season'
import { useKeySequence } from './use-key-sequence'
import { CheatCode } from './cheat/cheat-code'
import { useFindsStore } from './finds.store'
import { syncFromServer } from './sync-server'
import { useSessionStore } from '@/lib/session.store'

const HalloweenMenu = lazy(() => import('./menu/halloween-menu'))
const SpookyMode = lazy(() => import('./spooky/spooky-mode'))
const HalloweenEffects = lazy(() => import('./effects/halloween-effects'))

export const KONAMI = [
  'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
  'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a',
] as const

export const SPOOKY_ATTRIBUTE = 'data-halloween'

function clearSpooky(): void {
  useFindsStore.getState().setSpooky(false)
  document.documentElement.removeAttribute(SPOOKY_ATTRIBUTE)
}

const MAX_TIMEOUT_MS = 2 ** 31 - 1

/** Arms a timer for the season end, re-arming in chunks when the delay exceeds setTimeout's limit. */
function scheduleSeasonEnd(onEnd: () => void): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined
  const arm = (): void => {
    const remaining = SEASON_END.getTime() - Date.now()
    if (remaining <= 0) {
      onEnd()
      return
    }
    timer = setTimeout(arm, Math.min(remaining, MAX_TIMEOUT_MS))
  }
  arm()
  return () => {
    if (timer) clearTimeout(timer)
  }
}

interface LiveGateProps {
  readonly email: string
}

/** Everything that exists only while the season is open. Unmounted wholesale at expiry. */
function LiveGate({ email }: LiveGateProps): ReactElement {
  const spookyOn = useFindsStore((s) => s.spookyOn)
  const [open, setOpen] = useState(false)
  const [everOpened, setEverOpened] = useState(false)

  useEffect(() => {
    useFindsStore.getState().bindEmail(email)
    void syncFromServer()
  }, [email])

  useEffect(() => {
    if (spookyOn) document.documentElement.setAttribute(SPOOKY_ATTRIBUTE, 'on')
    else document.documentElement.removeAttribute(SPOOKY_ATTRIBUTE)
    return () => document.documentElement.removeAttribute(SPOOKY_ATTRIBUTE)
  }, [spookyOn])

  // The sequence reads the state at the moment it is typed, so it follows a ref.
  const openRef = useRef(false)
  useEffect(() => {
    openRef.current = open
  }, [open])

  // An open menu closes first; otherwise, in Spooky Mode, the code leaves it;
  // otherwise it enters Spooky Mode and opens the menu, so closing the menu keeps the theme.
  const handleMatch = useCallback((): void => {
    if (!isSeasonOpen()) return
    if (openRef.current) {
      setOpen(false)
      return
    }
    if (useFindsStore.getState().spookyOn) {
      useFindsStore.getState().setSpooky(false)
      return
    }
    useFindsStore.getState().setSpooky(true)
    setEverOpened(true)
    setOpen(true)
  }, [])
  const close = useCallback((): void => setOpen(false), [])
  useKeySequence(KONAMI, handleMatch)

  return (
    <Suspense fallback={null}>
      {spookyOn ? <SpookyMode /> : null}
      <HalloweenEffects />
      {everOpened ? <HalloweenMenu open={open} onClose={close} /> : null}
      <CheatCode />
    </Suspense>
  )
}

function ActiveGate(): ReactElement | null {
  const email = useSessionStore((s) => s.user.email)
  const [expired, setExpired] = useState(false)

  useEffect(() => {
    if (expired) return undefined
    const expire = (): void => {
      clearSpooky()
      setExpired(true)
    }
    const check = (): void => {
      if (!isSeasonOpen()) expire()
    }
    // Timers are throttled in background tabs, so re-check on visibility and keypress too.
    const cancelTimer = scheduleSeasonEnd(expire)
    document.addEventListener('visibilitychange', check)
    window.addEventListener('keydown', check)
    return () => {
      cancelTimer()
      document.removeEventListener('visibilitychange', check)
      window.removeEventListener('keydown', check)
    }
  }, [expired])

  if (expired) return null
  return <LiveGate email={email} />
}

/** Renders nothing, and loads nothing, unless the user is eligible. */
export function HalloweenGate(): ReactElement | null {
  const eligible = useHalloweenEligible()
  return eligible ? <ActiveGate /> : null
}
